// Sale window rules ("okno sprzedaży") — shared between the per-variant
// defaults in StepBookingSettings and the slot form in StepAvailability.
//
// Each edge is configured as exactly one of:
//
//   * `${attribute}_offset_seconds` — "z wyprzedzeniem", counted back from
//     the slot start; the backend resolves it into a concrete datetime for
//     every slot it generates,
//   * `${attribute}`               — a fixed datetime ("od daty" / "do daty").

export const SALE_WINDOW_EDGES = [
  {
    attribute: "sale_starts_at",
    edge: "start",
    labelKey: "papp.components.SaleWindowRuleFormGroup.sale_starts_at_label",
  },
  {
    attribute: "sale_ends_at",
    edge: "end",
    labelKey: "papp.components.SaleWindowRuleFormGroup.sale_ends_at_label",
  },
];

// Slot attributes pre-filled from the variant's defaults when the operator
// starts adding new dates.
export function saleWindowFromVariantDefaults(product_variant) {
  return SALE_WINDOW_EDGES.reduce(
    (attributes, { attribute }) => ({
      ...attributes,
      [`${attribute}_offset_seconds`]: product_variant[`default_${attribute}_offset_seconds`],
      [attribute]: product_variant[`default_${attribute}`],
    }),
    {}
  );
}

// The relative part of the rule, sent alongside slot_attributes so the
// backend can stamp every generated slot with the same window.
//
// Both edges are always sent, offset or null, and the value is taken as-is:
// null means "this edge is a fixed datetime (or nothing)", and the backend
// reads the datetime out of slot_attributes. A saved slot carries both its
// offset (mirrored from the schedule) and the datetime that offset resolved
// to, so inferring the rule from the datetime would silently downgrade every
// series to a single shared date on the next save.
//
// Only forms that actually edit the rule may send this — see
// ProductScheduleSlotsRepository#_serializeSlotToData.
export function saleWindowOffsetAttributes(slot) {
  return SALE_WINDOW_EDGES.reduce((attributes, { attribute }) => {
    const offsetSeconds = slot[`${attribute}_offset_seconds`];
    return {
      ...attributes,
      [`${attribute}_offset_seconds`]: offsetSeconds === undefined ? null : offsetSeconds,
    };
  }, {});
}

// Badge shown under the sale window fields. `within_sale_window` from the
// API answers one question only — is *now* between sale_starts_at and
// sale_ends_at — so a date that has already passed can still sit inside its
// window and the panel used to advertise it as sellable. Checkout rejects it
// anyway (ProductScheduleSlot#orderable? also requires in_future?), and
// "poza oknem sprzedaży" would name the wrong reason, so a past date gets a
// state of its own.
export function saleWindowStatus(slot) {
  if (slot.within_sale_window === null || slot.within_sale_window === undefined) return null;
  // Same expression as ProductScheduleSlotDecorator#in_future, read off
  // starts_at rather than through the getter: the general reservations
  // calendar puts `{ ...slot }` back into its form state on every field
  // change, which keeps the API fields and drops everything defined on the
  // prototype. Through the getter the badge would call a future date passed
  // the moment anyone touched the sale window fields.
  if (!moment(slot.starts_at).isAfter()) {
    return { styleName: "sale-window-status-past", labelKey: "within_sale_window_past" };
  }

  return slot.within_sale_window
    ? { styleName: "sale-window-status-ok", labelKey: "within_sale_window_yes" }
    : { styleName: "sale-window-status-blocked", labelKey: "within_sale_window_no" };
}

// Mirrors SaleWindowRule.resolve on the backend: a relative rule is counted
// back from the slot's own start, so every date of a series ends up with its
// own window. Used only to preview that datetime next to the rule — the
// backend recomputes it on save. Resolved in the slot's own zone rather than
// against the offset carried by starts_at: both name the same instant, but a
// window that reaches back across a DST change is only labelled with the
// clock the panel shows for the saved value when the zone gets to pick the
// offset.
export function resolveSaleWindowOffset({ slotStartsAt, offsetSeconds, timeZoneName }) {
  if (offsetSeconds === null || offsetSeconds === undefined || !slotStartsAt) return null;

  return moment
    .tz(slotStartsAt, timeZoneName)
    .subtract(offsetSeconds, "seconds")
    .format(moment.RUBY_DATETIME_FORMAT);
}



// WEBPACK FOOTER //
// ./src/lib/utils/saleWindow.js